import React, { useMemo } from "react";
import { useTranslate, useGetIdentity } from "@refinedev/core";
import {
  DashboardOutlined,
  UnorderedListOutlined,
  SettingOutlined,
  AppstoreOutlined,
  EuroCircleOutlined,
  FileProtectOutlined,
  BellOutlined,
  MedicineBoxOutlined,
  TagsOutlined,
  ContainerOutlined,
  IdcardOutlined,
  BarChartOutlined,
  ShopOutlined,
  FormOutlined,
  SafetyCertificateOutlined,
  ClusterOutlined,
  ShoppingOutlined,
  DeploymentUnitOutlined,
} from "@ant-design/icons";
import { useAdmin, useCustomer, useSuperAdmin, useTuner } from "../../hooks/auth";

/**
 * Single source of truth for the application navigation tree.
 *
 * Both the desktop sider (`sider.tsx`) and the mobile drawer (`DrawerMenu.tsx`)
 * render from this hook, so the two can no longer drift apart.
 *
 * Nodes are organised by *scope of effect* — "whose reality does this change?":
 *   - operational data (Catalogue, Équipe) sits at top level, not under Configuration
 *   - tenant-wide configuration lives under Configuration
 *   - cross-tenant configuration lives under Plateforme (superadmin only)
 */
export interface MenuNode {
  /** Unique menu key. Must be unique across the whole tree — Ant Menu keys collide otherwise. */
  key: string;
  label: string;
  route: string;
  icon: React.ReactNode;
  children: MenuNode[];
  /**
   * Refine resource backing this node, when there is one. Currently unused by the
   * renderers: menu visibility is decided by the role checks below, because
   * `accessControlProvider` is still allow-all for admins. Once it grows a real
   * resource × role matrix, the renderers can wrap each node in `<CanAccess>`
   * and the role branching in this file can be deleted node by node.
   */
  resource?: string;
  /** Extra paths that should light this node up as selected. */
  matchPaths?: string[];
}

const node = (n: Partial<MenuNode> & Pick<MenuNode, "key" | "label" | "route">): MenuNode => ({
  icon: <UnorderedListOutlined className="h-4 w-4" />,
  children: [],
  ...n,
});

export const useMenuTree = (): MenuNode[] => {
  const translate = useTranslate();
  const isAdmin = useAdmin();
  const isCustomer = useCustomer();
  const isSuperAdmin = useSuperAdmin();
  const isTuner = useTuner();
  const { data: user } = useGetIdentity();
  const accountId = (user as any)?.account?.id;
  const billingMode = (user as any)?.account?.billingMode;

  const adminItems = useMemo<MenuNode[]>(() => {
    const items: MenuNode[] = [
      node({
        key: "/",
        label: translate("dashboard.title", "Dashboard"),
        route: "/",
        icon: <DashboardOutlined className="h-4 w-4" />,
      }),
      node({
        key: "/orders",
        label: translate("orders.orders"),
        route: "/orders",
        icon: <ContainerOutlined className="h-4 w-4" />,
        resource: "orders",
      }),
      node({
        key: "/sav",
        label: translate("orders.sav"),
        route: "/sav",
        icon: <MedicineBoxOutlined className="h-4 w-4" />,
      }),
      node({
        key: "/accounts-menu",
        label: translate("accounts.accounts"),
        route: "/accounts-menu",
        icon: <IdcardOutlined className="h-4 w-4" />,
        children: [
          node({
            key: "/accounts",
            label: translate("accounts.accountsList", "Accounts List"),
            route: "/accounts",
            icon: <UnorderedListOutlined className="h-4 w-4" />,
            resource: "accounts",
          }),
          node({
            key: "/account-rating",
            label: translate("accountRating.title", "Usage by Client"),
            route: "/account-rating",
            icon: <BarChartOutlined className="h-4 w-4" />,
          }),
          node({
            key: "/account-groups",
            label: translate("account-groups.account-groups", "Account groups"),
            route: "/account-groups",
            icon: <ClusterOutlined className="h-4 w-4" />,
            resource: "account-groups",
          }),
        ],
      }),
      node({
        key: "/transactions",
        label: translate("transactions.transactions"),
        route: "/transactions",
        icon: <EuroCircleOutlined className="h-4 w-4" />,
        resource: "transactions",
      }),
      // ---- Catalogue: business data, deliberately NOT under Configuration ----
      node({
        key: "/catalog",
        label: translate("catalog.catalog", "Catalog"),
        route: "/catalog",
        icon: <ShoppingOutlined className="h-4 w-4" />,
        matchPaths: [
          "/products",
          "/pricing-plans",
          "/credit-packs",
          "/product-rules",
        ],
      }),
      node({
        key: "/notifications",
        label: translate("notifications.notifications"),
        route: "/notifications",
        icon: <BellOutlined className="h-4 w-4" />,
      }),

      // ---- Configuration: tenant-wide settings ----
      node({
        key: "/configuration",
        label: translate("configuration.configuration", "Configuration"),
        route: "/configuration",
        icon: <SettingOutlined style={{ fontSize: 18 }} />,
        children: [
          node({
            key: "/configuration/organization",
            label: translate("configuration.organization", "Organization"),
            route: "/configuration/organization",
            icon: <ClusterOutlined className="h-4 w-4" />,
            matchPaths: ["/settings/general", "/invoice-settings", "/usage"],
          }),
          node({
            key: "/configuration/workflow",
            label: translate("configuration.workflow", "Production workflow"),
            route: "/configuration/workflow",
            icon: <DeploymentUnitOutlined className="h-4 w-4" />,
            matchPaths: [
              "/users",
              "/user-groups",
              "/team",
              "/assignment-rules",
              "/service-providers",
              "/automation-tasks",
              "/automation",
            ],
          }),
        ],
      }),
    ];

    // ---- Plateforme: cross-tenant, superadmin only ----
    if (isSuperAdmin) {
      items.push(
        node({
          key: "/platform",
          label: translate("platform.platform", "Platform"),
          route: "/platform",
          icon: <SafetyCertificateOutlined style={{ fontSize: 18 }} />,
          children: [
            node({
              key: "/tenants",
              label: translate("tenants.tenants", "Tenants"),
              route: "/tenants",
              icon: <ShopOutlined className="h-4 w-4" />,
              resource: "tenants",
            }),
            node({
              key: "/forms",
              label: translate("forms.forms"),
              route: "/forms",
              icon: <FormOutlined className="h-4 w-4" />,
              resource: "forms",
            }),
            node({
              key: "/clients",
              label: translate("clients.clients", "Client credentials"),
              route: "/clients",
              icon: <SafetyCertificateOutlined className="h-4 w-4" />,
              resource: "clients",
            }),
          ],
        }),
      );
    }

    return items;
  }, [translate, isSuperAdmin, isAdmin]);

  const customerItems = useMemo<MenuNode[]>(() => {
    const items: MenuNode[] = [
      node({
        key: "/account-home",
        label: translate("dashboard.title", "Dashboard"),
        route: "/account-home",
        icon: <DashboardOutlined className="h-4 w-4" />,
      }),
      node({
        key: "/orders",
        label: translate("orders.orders"),
        route: `/accounts/${accountId}/orders/list`,
        icon: <ContainerOutlined className="h-4 w-4" />,
      }),
      node({
        key: "/tarifs",
        label: translate("tarifs.tarifs"),
        route: `/accounts/${accountId}/tarifs/list`,
        icon: <TagsOutlined className="h-4 w-4" />,
      }),
    ];

    // Invoices only for postpaid, transactions only for prepaid.
    if (billingMode === "postpaid") {
      items.push(
        node({
          key: "/invoices",
          label: translate("invoices.invoices"),
          route: `/accounts/${accountId}/invoices/list`,
          icon: <FileProtectOutlined className="h-4 w-4" />,
        }),
      );
    }
    if (billingMode === "prepaid") {
      items.push(
        node({
          key: "/transactions",
          label: translate("transactions.transactions"),
          route: `/accounts/${accountId}/transactions/list`,
          icon: <EuroCircleOutlined className="h-4 w-4" />,
        }),
      );
    }

    items.push(
      node({
        key: "/settings",
        label: translate("settings.myAccount", "My Account"),
        route: "/settings",
        icon: <SettingOutlined className="h-4 w-4" />,
      }),
    );

    return items;
  }, [translate, accountId, billingMode]);

  const tunerItems = useMemo<MenuNode[]>(
    () => [
      node({
        key: "/orders",
        label: translate("orders.orders"),
        route: "/orders",
        icon: <ContainerOutlined className="h-4 w-4" />,
      }),
    ],
    [translate],
  );

  if (isAdmin || isSuperAdmin) return adminItems;
  if (isCustomer) return customerItems;
  if (isTuner) return tunerItems;
  return [];
};

/**
 * Resolve which menu key should be highlighted for a pathname, honouring
 * `matchPaths` so legacy routes and hub sub-routes still light up their node.
 */
export const findSelectedKey = (
  items: MenuNode[],
  pathname: string,
): string | undefined => {
  let best: { key: string; score: number } | undefined;

  const walk = (nodes: MenuNode[]) => {
    for (const n of nodes) {
      for (const candidate of [n.route, ...(n.matchPaths ?? [])]) {
        if (!candidate || candidate === "/accounts-menu") continue;
        const exact = pathname === candidate;
        const prefix = candidate !== "/" && pathname.startsWith(`${candidate}/`);
        if (exact || prefix) {
          const score = candidate.length + (exact ? 1000 : 0);
          if (!best || score > best.score) best = { key: n.key, score };
        }
      }
      if (n.children.length) walk(n.children);
    }
  };

  walk(items);
  if (!best && pathname === "/") return "/";
  return best?.key;
};

/** Keys of every ancestor group, so submenus start open on the active branch. */
export const findOpenKeys = (items: MenuNode[], selectedKey?: string): string[] => {
  if (!selectedKey) return [];
  const open: string[] = [];

  const walk = (nodes: MenuNode[], trail: string[]): boolean => {
    for (const n of nodes) {
      if (n.key === selectedKey) {
        open.push(...trail);
        return true;
      }
      if (n.children.length && walk(n.children, [...trail, n.key])) return true;
    }
    return false;
  };

  walk(items, []);
  return open;
};
